import { Injectable, computed, inject, signal } from '@angular/core';
import { EMPTY, finalize, tap } from 'rxjs';

import {
  AuthService,
  RoomDetailsModel,
  RoomFiltersModel,
  RoomLayoutsModel,
  RoomQueryParams,
  RoomsApiService,
  RoomsListModel,
} from '@bookly/shared';
@Injectable({
  providedIn: 'root',
})
export class RoomsService {
  readonly #roomsApiService = inject(RoomsApiService);
  readonly #authService = inject(AuthService);
  readonly #isRoomListLoading = signal(false);
  readonly #isRoomFiltersLoading = signal(false);
  readonly #isRoomDetailsLoading = signal(false);
  readonly #roomsList = signal<RoomsListModel[] | null>(null);
  readonly #roomDetails = signal<RoomDetailsModel | null>(null);
  readonly #roomLayouts = signal<RoomLayoutsModel[] | null>(null);
  readonly #roomFiltersData = signal<RoomFiltersModel | null>(null);
  readonly #totalSize = signal(0);
  readonly #currentPage = signal(0);
  readonly #pageSize = signal(10);
  /** Set to true when a page returns fewer items than requested — reliable end-of-data marker. */
  readonly #reachedEnd = signal(false);
  /** Filters used for the most recent page-0 load, reused by loadMoreRooms. */
  #lastFilters: RoomQueryParams | undefined;

  get roomsList() {
    return this.#roomsList.asReadonly();
  }

  get roomDetails() {
    return this.#roomDetails.asReadonly();
  }

  get roomLayouts() {
    return this.#roomLayouts.asReadonly();
  }

  get isRoomListLoading() {
    return this.#isRoomListLoading.asReadonly();
  }

  get isRoomFiltersLoading() {
    return this.#isRoomFiltersLoading.asReadonly();
  }

  get isRoomDetailsLoading() {
    return this.#isRoomDetailsLoading.asReadonly();
  }

  get roomFiltersData() {
    return this.#roomFiltersData.asReadonly();
  }

  readonly hasMoreRooms = computed(
    () => this.#roomsList() !== null && !this.#reachedEnd()
  );

  get totalRoomsCount() {
    return this.#totalSize.asReadonly();
  }

  get currentPage() {
    return this.#currentPage.asReadonly();
  }

  public getRooms(filters?: RoomQueryParams, page = 0, pageSize?: number) {
    const resolvedPageSize = pageSize ?? this.#pageSize();
    this.#isRoomListLoading.set(true);
    if (pageSize !== undefined) {
      this.#pageSize.set(pageSize);
    }
    if (page === 0) {
      this.#lastFilters = filters;
      // Drop stale cards immediately so a new filter/search renders loading placeholders.
      this.#roomsList.set(null);
    }
    return this.#roomsApiService.getRooms(filters, page, resolvedPageSize).pipe(
      tap(response => {
        if (page === 0) {
          this.#totalSize.set(response.totalSize);
          this.#reachedEnd.set(response.content.length < resolvedPageSize);
        } else {
          // Partial page (or empty) means no more data
          if (response.content.length < resolvedPageSize) {
            this.#reachedEnd.set(true);
          }
        }
        this.#currentPage.set(page);
        const rooms =
          this.#authService.roles.length > 0
            ? response.content
            : response.content.filter(room => room.active);
        if (page === 0) {
          this.#roomsList.set(rooms);
        } else {
          this.#roomsList.update(existing => {
            if (!existing) {
              return rooms;
            }

            const existingDexIds = new Set(existing.map(room => room.dexId));
            const uniqueNewRooms = rooms.filter(
              room => !existingDexIds.has(room.dexId)
            );

            return [...existing, ...uniqueNewRooms];
          });
        }
      }),
      finalize(() => this.#isRoomListLoading.set(false))
    );
  }

  /** Loads the next page using the same filters as the last page-0 load. */
  public loadMoreRooms() {
    return this.getRooms(this.#lastFilters, this.#currentPage() + 1);
  }

  public clearRooms(): void {
    this.#roomsList.set(null);
    this.#totalSize.set(0);
    this.#reachedEnd.set(false);
  }

  public updateRoomFavorite(dexId: string, isFavorite: boolean): void {
    this.#roomsList.update(rooms =>
      rooms
        ? rooms.map(room =>
            room.dexId === dexId ? { ...room, isFavorite } : room
          )
        : null
    );
  }

  public updateRoomDetailsFavorite(isFavorite: boolean): void {
    this.#roomDetails.update(details =>
      details ? { ...details, isFavorite } : null
    );
  }

  public removeRoom(dexId: string): void {
    this.#roomsList.update(rooms =>
      rooms ? rooms.filter(room => room.dexId !== dexId) : null
    );
  }

  public getRoomFilters() {
    if (this.#roomFiltersData()) {
      return EMPTY;
    }
    this.#isRoomFiltersLoading.set(true);
    return this.#roomsApiService.getRoomFilters().pipe(
      tap(filters => {
        this.#roomFiltersData.set(filters);
      }),
      finalize(() => this.#isRoomFiltersLoading.set(false))
    );
  }

  public getRoomData(roomId: string) {
    this.#roomDetails.set(null);
    this.#roomLayouts.set(null);
    this.#isRoomDetailsLoading.set(true);
    return this.#roomsApiService.getRoomDetails(roomId).pipe(
      tap(roomDetails => {
        this.#roomDetails.set(roomDetails);
        this.#roomLayouts.set(
          roomDetails.manageSpaceConfiguration?.layouts ?? []
        );
      }),
      finalize(() => this.#isRoomDetailsLoading.set(false))
    );
  }
}
